/**
 * Year 1 day resolver.
 *
 * Each src/data/raw/year1_{a,b}/month_NN.js file exports:
 *   - `exercises`  — keyed dict of exercise definitions for that month
 *   - `days`       — array of { day, week, title, title_pt, exercises: [{ref, overrides}] }
 *   - `monthMeta`  — { month, program, phase, phase_pt, days_range, session_target_seconds }
 *   - `resolveDay(dayNumber)` — returns { day, week, title, title_pt, exercises: [expanded] }
 *
 * This wrapper picks the right month file for a day (1..365), calls its
 * `resolveDay`, merges in `monthMeta` under `.meta`, and normalizes any
 * `type: 'timed'` exercises so ExercisePlayer doesn't double-count their
 * duration (the player's RELEASE state falls back to `hold` when `relax` is
 * missing, which for a 150-second timed hold would double the runtime).
 */

// Map raw month-file movement values to the uppercase names + lowercase
// `movementMode` that the legacy SessionScreen overview and ExercisePlayer
// expect. Raw files use lowercase 'squeeze' / 'release' / 'push' / 'neutral';
// the legacy pipeline wants 'SQUEEZE' / 'RELAX' / 'PUSH' as `movement` and
// 'squeeze' / 'relax' / 'push' / 'neutral' as `movementMode` (the latter
// feeds RING_SCALES in src/utils/playerEngine.js).
const MOVEMENT_MAP = {
  squeeze: { movement: 'SQUEEZE', movementMode: 'squeeze' },
  release: { movement: 'RELAX',   movementMode: 'relax'   },
  relax:   { movement: 'RELAX',   movementMode: 'relax'   },
  push:    { movement: 'PUSH',    movementMode: 'push'    },
  neutral: { movement: 'RELAX',   movementMode: 'relax'   },
}

// Normalize one expanded exercise so the legacy ExercisePlayer / SessionScreen
// run it correctly. Two fixes:
//  1. `type: 'timed'` has no `relax` field, which makes ExercisePlayer's
//     RELEASE state fall back to `hold` and double the exercise duration.
//     Force `relax: 0` so RELEASE fires instantly.
//  2. Normalize `movement` (SessionScreen overview) and add `movementMode`
//     (ExercisePlayer ring animation) from the raw lowercase values.
function normalizeExercise(ex) {
  const mm = ex.movement && MOVEMENT_MAP[ex.movement]
  const out = { ...ex }
  if (mm) {
    out.movement = mm.movement
    if (out.movementMode == null) out.movementMode = mm.movementMode
  }
  if (out.type === 'timed' && out.relax == null) {
    out.relax = 0
  }
  return out
}

export async function resolveYear1Day(dayNumber, path = 'A') {
  const clamped = Math.max(1, Math.min(dayNumber | 0 || 1, 365))
  const month = Math.min(Math.ceil(clamped / 30), 12)
  const pad = String(month).padStart(2, '0')

  const mod = path === 'B'
    ? await import(`./raw/year1_b/month_${pad}.js`)
    : await import(`./raw/year1_a/month_${pad}.js`)

  const dayData = mod.resolveDay(clamped)
  if (!dayData) return null

  return {
    ...dayData,
    exercises: dayData.exercises.map(normalizeExercise),
    meta: mod.monthMeta,
  }
}
